import { IonButton, IonMenu, IonMenuToggle} from '@ionic/react';
import { FC, useRef, useState } from 'react';
import { useInstance } from '@meraki-internal/react-dependency-injection';
import { createUseStyles } from 'react-jss';
import { HamburgerMenuIcon } from '../theme/Icons';
import { mediaMinWidth } from '../theme/utils';
import { LoadingPage } from '../LoadingPage';
import { AnonymousNavigationHeader, NavigationHeader } from './NavigationHeader';
import { NavigationContent } from './NavigationContent';
import { NavigationFooter } from './NavigationFooter';
import { NavigationMenuState } from './NavigationMenuState';
import { AppPrincipal, isAnonymousViewer } from '../auth/AppPrincipal';
import { ReminderBadge } from '../components/ReminderBadge';
import { IntercomState } from '../support/IntercomService';
import { useSubscription } from '@meraki-internal/state';
import { useBreakpoints } from '../theme/useBreakpoints';

const width = 375;
const useStyles = createUseStyles({
    menu: {
        '--max-width': `${width}px`,

        [mediaMinWidth.sm]: {
            '--min-width': `${width}px`,
        },

        '&::part(backdrop)': {
            backgroundColor: 'black',
            opacity: '0.55 !important'
        }
    }
});

type INavigationMenuToggle = {
    slot?: 'start' | 'end',
    noAppHeader?: boolean;
};

export const NavigationMenuToggle: FC<INavigationMenuToggle> = ({ slot, noAppHeader }) => {
    const intercomState = useInstance(IntercomState);
    useSubscription(() => intercomState);

    const { isAboveBreakpoint } = useBreakpoints();

    // on larger screens render placeholder (hamburger is shown in side nav instead)
    if (isAboveBreakpoint('lg')) {
        return <div slot={slot} style={{width: 50}} />;
    }

    const unreadIntercomCount = intercomState.state.unreadCount;
    let badgeStyle: any = {
        top: 12,
        left: 29,
        position: 'absolute',
        pointerEvents: 'none'
    };

    if (noAppHeader) {
        badgeStyle = {
            ...badgeStyle,
            top: 0,
            left: 28
        };
    }

    // this component will auto-hide if the corresponding menu is disabled
    // https://ionicframework.com/docs/api/menu-toggle
    return (
        <IonMenuToggle slot={slot} menu="navigation-menu">
            <IonButton fill="clear" id="navigation-menu-button" data-type="menu-nav-item">
                <HamburgerMenuIcon color="light" />
            </IonButton>
            {unreadIntercomCount > 0 &&
                <div style={badgeStyle}>
                    <ReminderBadge size="small" />
                </div>
            }
        </IonMenuToggle>
    );
};

export const NavigationMenu: FC<{contentId: string}> = ({ contentId }) => {
    const classes = useStyles();
    const navMenuState = useInstance(NavigationMenuState);

    const isAnonymous = isAnonymousViewer(useInstance(AppPrincipal));

    const closeBtnRef = useRef<HTMLDivElement>(null);

    const [deleting, setDeleting] = useState(false);

    const closeMenu = () => {
        // TODO: shouldn't need to be this complex, either get the correct menu ref or use menuController
        closeBtnRef.current?.click();
    };

    if (deleting) {
        return (
            <LoadingPage showLogo />
        );
    }

    return (
        <IonMenu
            data-id="navigation-menu"
            menuId="navigation-menu"
            contentId={contentId}
            className={classes.menu}
            onIonWillOpen={() => navMenuState.setOpen(true)}
            onIonDidClose={() => navMenuState.setOpen(false)}
            swipeGesture={false}
        >
            {!isAnonymous && (
                <NavigationHeader
                    closeBtnRef={closeBtnRef}
                    onDelete={() => setDeleting(true)}
                />
            )}
            {isAnonymous && (
                <AnonymousNavigationHeader closeBtnRef={closeBtnRef} />
            )}

            <NavigationContent
                closeMenuHandler={closeMenu}
            />

            <NavigationFooter closeMenu={closeMenu} />
        </IonMenu>
    );
};
